/** @format */

import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import {
    TrendingUp,
    TrendingDown,
    Savings,
    Speed,
    EventAvailable,
    InsightsOutlined,
} from "@mui/icons-material";

import { CollectSectionCard } from "../styles";
import { colors } from "../../../utility/types/constant";
import { IRecoveryImpact, ImpactRange } from "../../../utility/types/collectTypes";
import { submitGetRecoveryImpact } from "../../../redux/services/collectService";

const FONT = "'Montserrat', sans-serif";

interface Props {
    businessId: string | null;
}

const RANGE_OPTIONS: { value: ImpactRange; label: string }[] = [
    { value: "2w", label: "Last 2 weeks" },
    { value: "4w", label: "Last 4 weeks" },
    { value: "3m", label: "Last 3 months" },
    { value: "6m", label: "Last 6 months" },
    { value: "12m", label: "Last 12 months" },
];

function formatMoney(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${currency || "$"}${Math.round(amount)}`;
    }
}

interface TileProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    sub?: React.ReactNode;
    accent: string;
}

const StatTile: React.FC<TileProps> = ({ icon, label, value, sub, accent }) => (
    <Box
        sx={{
            flex: 1,
            minWidth: 0,
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            p: 1.75,
            backgroundColor: "#fff",
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <Box sx={{ color: accent, display: "inline-flex" }}>{icon}</Box>
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                }}
            >
                {label}
            </Typography>
        </Box>
        <Typography
            sx={{ fontFamily: FONT, fontSize: "1.5rem", fontWeight: 800, color: "#111827", lineHeight: 1.1 }}
        >
            {value}
        </Typography>
        {sub && <Box sx={{ mt: 0.5 }}>{sub}</Box>}
    </Box>
);

const RecoveryImpactPanel: React.FC<Props> = ({ businessId }) => {
    const [range, setRange] = useState<ImpactRange>("3m");
    const [data, setData] = useState<IRecoveryImpact | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        if (!businessId) {
            setData(null);
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }

        setLoading(true);
        setError("");
        setData(null);

        submitGetRecoveryImpact(businessId, range)
            .then((response) => {
                if (!cancelled) setData(response);
            })
            .catch((error: any) => {
                if (!cancelled) {
                    setError(error?.message || "Failed to load recovery impact.");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [businessId, range]);

    if (!businessId) return null;

    const currency = data?.currency || "USD";
    const hasRecovery = !!data && data.recovered.count > 0;

    // Momentum chip for recovered amount.
    const renderMomentum = () => {
        if (!data || data.recoveredDeltaPct === null) return null;
        const up = data.recoveredDeltaPct >= 0;
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                {up ? (
                    <TrendingUp sx={{ fontSize: "1rem", color: "#15803d" }} />
                ) : (
                    <TrendingDown sx={{ fontSize: "1rem", color: "#b45309" }} />
                )}
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        color: up ? "#15803d" : "#b45309",
                    }}
                >
                    {up ? "+" : ""}
                    {data.recoveredDeltaPct}% vs prior period
                </Typography>
            </Box>
        );
    };

    // Improvement chip for days-to-get-paid.
    const renderSpeedTrend = () => {
        if (!data || data.avgDaysImprovement === null) return null;
        const faster = data.avgDaysImprovement > 0;
        const same = data.avgDaysImprovement === 0;
        if (same) {
            return (
                <Typography sx={{ fontFamily: FONT, fontSize: "0.74rem", color: "#6b7280" }}>
                    Same speed as prior period
                </Typography>
            );
        }
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                {faster ? (
                    <TrendingDown sx={{ fontSize: "1rem", color: "#15803d" }} />
                ) : (
                    <TrendingUp sx={{ fontSize: "1rem", color: "#b45309" }} />
                )}
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        color: faster ? "#15803d" : "#b45309",
                    }}
                >
                    {Math.abs(data.avgDaysImprovement)} days {faster ? "faster" : "slower"}
                </Typography>
            </Box>
        );
    };

    const headline = (() => {
        if (!data || !hasRecovery) return "";
        const amount = formatMoney(data.recovered.amount, currency);
        const count = data.recovered.count;
        const speed =
            data.avgDaysToGetPaid !== null
                ? ` Payments arrived about ${Math.round(data.avgDaysToGetPaid)} days after invoicing on average.`
                : "";
        return `You collected ${amount} across ${count} invoice${count === 1 ? "" : "s"} this period.${speed}`;
    })();

    return (
        <CollectSectionCard>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 0.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InsightsOutlined sx={{ color: colors.primary }} />
                    <Typography
                        sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.05rem", color: "#111827" }}
                    >
                        Recovery Impact
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        value={range}
                        onChange={(e) => setRange(e.target.value as ImpactRange)}
                        sx={{
                            fontFamily: FONT,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            borderRadius: "10px",
                            "& .MuiSelect-select": { py: 0.7 },
                        }}
                    >
                        {RANGE_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value} sx={{ fontFamily: FONT, fontSize: "0.8rem" }}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Typography
                sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#6b7280", mb: 1.5 }}
            >
                What the platform delivered cash brought in and how fast.
            </Typography>

            {error && (
                <Typography sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#b91c1c", mb: 1 }}>
                    {error}
                </Typography>
            )}

            {loading && !data ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
            ) : !hasRecovery ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Savings sx={{ fontSize: "2rem", color: "#cbd5e1", mb: 1 }} />
                    <Typography
                        sx={{ fontFamily: FONT, fontSize: "0.9rem", color: "#374151", fontWeight: 700 }}
                    >
                        No QuickBooks payments in this period yet
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: "0.76rem", color: "#9ca3af" }}>
                        As payments are applied to invoices, your recovery impact will show up here.
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
                        <StatTile
                            icon={<Savings fontSize="small" />}
                            label="Collected"
                            value={formatMoney(data!.recovered.amount, currency)}
                            accent="#15803d"
                            sub={
                                <Box>
                                    <Typography sx={{ fontFamily: FONT, fontSize: "0.74rem", color: "#6b7280" }}>
                                        {data!.recovered.count} invoice{data!.recovered.count === 1 ? "" : "s"} with payments
                                    </Typography>
                                    {renderMomentum()}
                                </Box>
                            }
                        />
                        <StatTile
                            icon={<Speed fontSize="small" />}
                            label="Avg days to payment"
                            value={
                                data!.avgDaysToGetPaid !== null
                                    ? `${Math.round(data!.avgDaysToGetPaid)}d`
                                    : "—"
                            }
                            accent={colors.primary}
                            sub={renderSpeedTrend()}
                        />
                        <StatTile
                            icon={<EventAvailable fontSize="small" />}
                            label="On-time recovery"
                            value={data!.onTimeRate !== null ? `${data!.onTimeRate}%` : "—"}
                            accent="#1d4ed8"
                            sub={
                                <Tooltip
                                    title="Share of invoices whose latest payment in this period arrived on or before the due date."
                                    arrow
                                >
                                    <Typography
                                        sx={{ fontFamily: FONT, fontSize: "0.74rem", color: "#6b7280", cursor: "help" }}
                                    >
                                        settled by due date
                                    </Typography>
                                </Tooltip>
                            }
                        />
                    </Box>

                    {headline && (
                        <Typography
                            sx={{ fontFamily: FONT, fontSize: "0.82rem", color: "#374151", mt: 1.75, lineHeight: 1.55 }}
                        >
                            {headline}
                        </Typography>
                    )}
                </>
            )}
        </CollectSectionCard>
    );
};

export default RecoveryImpactPanel;
